/**
 * Fiscal Year Modal
 *
 * Modal for manual data entry - user specifies fiscal year dates.
 * After validation, creates an empty ParsedSieData structure and navigates to Step 2.
 */

import { useState, useEffect } from "react";
import { Calendar, Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";

interface FiscalYearModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (fiscalYearStart: string, fiscalYearEnd: string) => Promise<void>;
  /** Pre-fill with existing dates if available */
  initialStartDate?: string;
  initialEndDate?: string;
}

/**
 * Validates fiscal year dates according to Swedish rules:
 * - Duration must be 1-18 months
 * - End date must be after start date
 * - Dates must be valid
 */
function validateFiscalYear(
  startDate: string,
  endDate: string
): { valid: boolean; error?: string; months?: number } {
  if (!startDate || !endDate) {
    return { valid: false, error: "Båda datum måste anges" };
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { valid: false, error: "Ogiltigt datumformat" };
  }

  if (end <= start) {
    return { valid: false, error: "Slutdatum måste vara efter startdatum" };
  }

  // Calculate months difference
  const months =
    (end.getFullYear() - start.getFullYear()) * 12 +
    (end.getMonth() - start.getMonth()) +
    1;

  if (months < 1 || months > 18) {
    return {
      valid: false,
      error: `Räkenskapsåret måste vara 1-18 månader (nu: ${months} månader)`,
      months,
    };
  }

  return { valid: true, months };
}

/**
 * Get default fiscal year dates (calendar year)
 */
function getDefaultFiscalYear(): { start: string; end: string } {
  const now = new Date();
  const year = now.getFullYear();

  // If we're in the first quarter, default to previous year
  const fiscalYear = now.getMonth() < 3 ? year - 1 : year;

  return {
    start: `${fiscalYear}-01-01`,
    end: `${fiscalYear}-12-31`,
  };
}

export function FiscalYearModal({
  isOpen,
  onClose,
  onConfirm,
  initialStartDate,
  initialEndDate,
}: FiscalYearModalProps) {
  const defaults = getDefaultFiscalYear();

  const [startDate, setStartDate] = useState(initialStartDate || defaults.start);
  const [endDate, setEndDate] = useState(initialEndDate || defaults.end);
  const [isSaving, setIsSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartDate(initialStartDate || defaults.start);
      setEndDate(initialEndDate || defaults.end);
      setTouched(false);
      setIsSaving(false);
    }
  }, [isOpen, initialStartDate, initialEndDate, defaults.start, defaults.end]);

  const validation = validateFiscalYear(startDate, endDate);

  const handleConfirm = async () => {
    setTouched(true);

    if (!validation.valid) {
      toast.error(validation.error || "Ogiltiga datum");
      return;
    }

    setIsSaving(true);
    try {
      await onConfirm(startDate, endDate);
      // onConfirm handles navigation, we just close
      onClose();
    } catch (err) {
      console.error("Failed to create manual report:", err);
      toast.error("Kunde inte skapa rapporten");
    } finally {
      setIsSaving(false);
    }
  };

  // Quick presets for common fiscal years
  const presets = [
    {
      label: "Kalenderår 2024",
      start: "2024-01-01",
      end: "2024-12-31",
    },
    {
      label: "Kalenderår 2025",
      start: "2025-01-01",
      end: "2025-12-31",
    },
    {
      label: "Brutet 2024 (jul-jun)",
      start: "2024-07-01",
      end: "2025-06-30",
    },
    {
      label: "Brutet 2024 (sep-aug)",
      start: "2024-09-01",
      end: "2025-08-31",
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ange räkenskapsår"
      size="md"
    >
      <div className="space-y-6">
        {/* Info text */}
        <p className="text-sm text-gray-600">
          Ange räkenskapsårets start- och slutdatum för den årsredovisning du vill skapa.
          Räkenskapsåret kan vara 1-18 månader.
        </p>

        {/* Quick presets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Snabbval
          </label>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  setStartDate(preset.start);
                  setEndDate(preset.end);
                  setTouched(false);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  startDate === preset.start && endDate === preset.end
                    ? "bg-primary-100 border-primary-300 text-primary-700"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Date inputs */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="fiscal-start"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Startdatum
            </label>
            <input
              type="date"
              id="fiscal-start"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setTouched(true);
              }}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label
              htmlFor="fiscal-end"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Slutdatum
            </label>
            <input
              type="date"
              id="fiscal-end"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setTouched(true);
              }}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Validation feedback */}
        {touched && (
          <div
            className={`p-3 rounded-lg flex items-start gap-2 ${
              validation.valid
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            {validation.valid ? (
              <>
                <CheckCircle size={18} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Giltigt räkenskapsår
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Perioden är {validation.months} månader
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{validation.error}</p>
              </>
            )}
          </div>
        )}

        {/* K2 2026 notice */}
        {validation.valid && new Date(startDate) >= new Date("2026-01-01") && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Nya K2-regler gäller
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  För räkenskapsår som börjar 2026 eller senare gäller nya restriktioner.
                  Du kommer att behöva bekräfta K2-behörighet i nästa steg.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={isSaving}
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!validation.valid || isSaving}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Skapar...
              </>
            ) : (
              <>
                <Calendar size={16} />
                Fortsätt
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}
